import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Table, Button, Input, Form, Typography, Alert } from 'antd';
import { EditOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useCurrentUser } from '../../../utils/CurrentUserContext';
import { GET_COMPANY_LABEL_SCHEMA } from '../../../utils/api/company';
import { GET_LABELS, CREATE_LABEL, UPDATE_LABEL } from '../../../utils/api/campaign';
import LabelCustomisationModal from '../../../components/modals/LabelCustomisationModal';
import { toast } from 'react-hot-toast';

const { Title, Text } = Typography;

const EditableName: React.FC<{
  record: any;
  onSave: (id: string, newName: string) => Promise<void>;
}> = ({ record, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(record.name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Label name cannot be empty');
      setValue(record.name);
      setEditing(false);
      return;
    }
    if (trimmed === record.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(record._id, trimmed);
      setEditing(false);
    } catch {
      setValue(record.name);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(record.name);
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Input
          ref={inputRef}
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={handleSave}
          onKeyDown={(e) => { if (e.key === 'Escape') handleCancel(); }}
          disabled={saving}
          style={{ flex: 1 }}
        />
        <Button
          type="link"
          size="small"
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={saving}
          style={{ color: '#52c41a' }}
        />
        <Button
          type="link"
          size="small"
          icon={<CloseOutlined />}
          onClick={handleCancel}
          disabled={saving}
          style={{ color: '#ff4d4f' }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{record.name}</span>
      <Button
        type="link"
        size="small"
        icon={<EditOutlined style={{ fontSize: 12 }} />}
        onClick={() => setEditing(true)}
        style={{ padding: '0 4px' }}
      />
    </div>
  );
};

const LabelsSettings: React.FC = () => {
  const { user } = useCurrentUser();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLabel, setEditingLabel] = useState<any>(null);
  const [form] = Form.useForm();

  // Queries
  const { data: schemaData, loading: schemaLoading } = useQuery(GET_COMPANY_LABEL_SCHEMA, {
    variables: { id: user.company._id },
    skip: !user?.company?._id,
  });

  const { data: labelsData, loading: labelsLoading, refetch: refetchLabels } = useQuery(GET_LABELS, {
    variables: { page: 1, perPage: 1000 },
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [createLabel, { loading: creating }] = useMutation(CREATE_LABEL);
  const [updateLabel] = useMutation(UPDATE_LABEL);

  const labelSchemaStr = schemaData?.company?.labelSchema;
  const labelSchema = useMemo(() => {
    if (!labelSchemaStr) return null;
    try {
      return typeof labelSchemaStr === 'string' ? JSON.parse(labelSchemaStr) : labelSchemaStr;
    } catch (e) {
      console.error("Failed to parse label schema", e);
      return null;
    }
  }, [labelSchemaStr]);

  const labels = labelsData?.labels?.items || [];

  const handleCreateSimpleLabel = async (values: { name: string }) => {
    try {
      await createLabel({
        variables: {
          record: {
            name: values.name,
            configuration: {}
          }
        }
      });
      toast.success('Label created');
      form.resetFields();
      refetchLabels();
    } catch (e: any) {
      toast.error(e.message || 'Failed to create label');
    }
  };

  const handleRenameLabel = async (id: string, newName: string) => {
    try {
      await updateLabel({
        variables: {
          _id: id,
          record: { name: newName }
        }
      });
      toast.success('Label renamed');
      refetchLabels();
    } catch (e: any) {
      toast.error(e.message || 'Failed to rename label');
      throw e;
    }
  };

  const handleUpdateConfiguration = async (formData: any) => {
    if (!editingLabel) return;
    
    // updateLabel wraps user's inputs into the configuration JSON scalar
    try {
      await updateLabel({
        variables: {
          _id: editingLabel._id,
          record: {
            name: editingLabel.name,
            configuration: formData
          }
        }
      });
      toast.success('Label updated');
      refetchLabels();
    } catch (e: any) {
      // Re-throw to catch inside the JSON schema modal for GraphQL Bubbling
      throw e;
    }
  };

  const openEditModal = (record: any) => {
    setEditingLabel(record);
    setIsModalVisible(true);
  };

  // Build dynamic columns based on labelSchema
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left' as const,
        render: (_: any, record: any) => (
          <EditableName record={record} onSave={handleRenameLabel} />
        ),
      }
    ];

    if (labelSchema && labelSchema.properties) {
      Object.keys(labelSchema.properties).forEach(key => {
        cols.push({
          title: labelSchema.properties[key].title || key,
          dataIndex: ['configuration', key],
          key,
          render: (value: any) => {
            if (typeof value === 'boolean') {
              return value ? 'Yes' : 'No';
            }
            if (value === null || value === undefined) return '-';
            return String(value);
          },
          onCell: () => ({
            style: {
              maxWidth: 150,
            },
          })
        });
      });
    }

    cols.push({
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button 
          type="link" 
          icon={<EditOutlined />} 
          onClick={() => openEditModal(record)}
          disabled={!labelSchema}
        >
          Configure
        </Button>
      ),
    });

    return cols;
  }, [labelSchema]);

  return (
    <div>
      <div className="mb-6 max-w-6xl">
        <Title level={4}>Labels</Title>
        <Alert 
          message="Updates Notice" 
          description="Changes are not automatically applied to ongoing campaigns. Reach out to support@contimo.io if you need that changes affect live campaigns." 
          type="info" 
          showIcon 
          className="mb-6"
        />
        
        <div className="mb-6 bg-gray-50 p-4 rounded-lg flex items-end shadow-sm border border-gray-100">
          <Form form={form} layout="vertical" onFinish={handleCreateSimpleLabel} className="w-full flex items-end gap-3">
            <Form.Item 
              name="name" 
              label={<Text strong>Quick Add New Label</Text>}
              rules={[{ required: true, message: 'Please enter label text' }]}
              className="mb-0 flex-1"
            >
              <Input placeholder="Enter label text..." />
            </Form.Item>
            <Form.Item className="mb-0">
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={creating}>
                Add
              </Button>
            </Form.Item>
          </Form>
        </div>

        <Table 
          columns={columns} 
          dataSource={labels} 
          rowKey="_id" 
          loading={labelsLoading || schemaLoading} 
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered
          className="bg-white"
        />
      </div>

      <LabelCustomisationModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        title={`Configure Label: ${editingLabel?.name}`}
        initialData={editingLabel?.configuration || {}}
        labelSchema={labelSchema}
        onSubmit={handleUpdateConfiguration}
      />
    </div>
  );
};

export default LabelsSettings;
